// The page for restaurants, /restaurants. Short enough to scan, then the sign-up form. The form
// stores a note the owner sees on the admin page, and the owner sets the restaurant up. No payment step.
import { sendRestaurantLead } from '../api.ts';
import { app, inputValue, logo, privacyLink, target } from '../dom.ts';
import { brand, titled } from '../../brand.ts';
import { RESTAURANT_NO_COST } from '../../no-cost-copy.ts';
import { onLeave } from '../router.ts';
import { trackStep } from '../track.ts';
import { PROPS_TITLE } from './restaurants-copy.ts';
import { calculatorSection, mountCalculator, utmFromUrl } from './calculator.ts';

const ico = (paths: string) =>
  `<svg class="r-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`;

const form = () => `
  <form id="leadForm" class="card stack" autocomplete="on" aria-labelledby="leadTitle">
    <h2 class="h" id="leadTitle" style="margin:0">Sign up</h2>
    <label class="field"><span class="lbl">Name</span><input id="lName" class="input" maxlength="60" autocomplete="name" required></label>
    <label class="field"><span class="lbl">Restaurant</span><input id="lRestaurant" class="input" maxlength="80" autocomplete="organization" required></label>
    <label class="field"><span class="lbl">Email or phone</span><input id="lContact" class="input" maxlength="120" autocomplete="email" inputmode="email" required></label>
    <label class="field"><span class="lbl">Message <span class="sub">(optional)</span></span><textarea id="lMessage" class="input" rows="3" maxlength="500" style="height:auto;padding-top:10px"></textarea></label>
    <div class="error" id="leadErr" role="alert" hidden></div>
    <button class="btn2" id="leadSend" type="submit">Sign up</button>
    <p class="sub" style="margin:0;font-size:12px">We only use this to reach you about ${brand.name}. ${privacyLink()}</p>
  </form>`;

// The explainer: the vertical cut on phones, the 16:9 cut everywhere else. Both are in public/media.
function videoTag(): string {
  const cut = window.matchMedia('(max-width: 700px)').matches ? '9x16' : '16x9';
  return `<video class="r-vid r-vid-${cut}" controls playsinline preload="none" poster="/media/restaurants-${cut}.jpg"
    src="/media/restaurants-${cut}.mp4" aria-label="${brand.name} for restaurants, a one-minute explainer"></video>`;
}

const step = (src: string, alt: string, caption: string) => `
  <li class="r-step">
    <div class="r-phone"><img src="${src}" alt="${alt}" width="390" height="520"></div>
    <p>${caption}</p>
  </li>`;

export function restaurantsView(): void {
  // The link's tags, kept now: the calculator lead saves them even after scrolling and edits.
  const utm = utmFromUrl();
  document.title = titled('For restaurants');
  app.classList.add('wide');
  onLeave(() => app.classList.remove('wide'));
  app.innerHTML = `
    <div class="bar">${logo}<a href="#leadForm" class="r-top-cta">Sign up</a></div>
    <div class="body landing r-page">
      <div class="stack r-hero" style="padding-top:12px">
        <span class="lbl">For restaurants</span>
        <h1 class="h h-xl">Less math at the table.</h1>
        <p class="lede" style="margin:0">Less waiting for your servers.</p>
        <a href="#leadForm" class="btn r-cta">Sign up</a>
        <p class="sub" style="margin:0">${RESTAURANT_NO_COST}</p>
      </div>

      <section class="stack r-video" aria-labelledby="videoTitle">
        <h2 class="h" id="videoTitle">See it in a minute</h2>
        ${videoTag()}
      </section>

      <section class="stack r-props" aria-labelledby="propsTitle">
        <h2 class="h" id="propsTitle">${PROPS_TITLE}</h2>
        <article class="r-prop">
          ${ico('<circle cx="12" cy="12" r="8"/><path d="M12 8v4l2.5 2"/><path d="M18.5 6.5l1.2-2.2.3 2.5"/>')}
          <div><b>Turn tables faster.</b><p>Nobody's splitting a check five ways at the end of the night, so your server moves on.</p></div>
        </article>
        <article class="r-prop">
          ${ico('<path d="M5 6h14M5 12h10M5 18h7"/>')}
          <div><b>Hear from your guests.</b><p>The person who paid can answer three quick questions after paying.</p></div>
        </article>
        <article class="r-prop">
          ${ico('<rect x="7" y="3" width="10" height="18" rx="2"/><path d="M11 18h2"/>')}
          <div><b>Bring the whole table back.</b><p>Anyone at the table can opt in to your texts, not just the person who paid.</p></div>
        </article>
      </section>

      <p class="r-aside">Less awkward. Guests tap what they had.</p>

      <section class="stack r-how" aria-labelledby="howTitle">
        <h2 class="h" id="howTitle">How it works</h2>
        <p class="sub r-how-note">Examples</p>
        <ol class="r-flow">
          ${step('/media/flow-scan.png', 'Guest screen after a scan.', 'Less time on the check.')}
          ${step('/media/flow-tap.png', 'Item list, one dish tapped.', 'Guests tap what they had.')}
          ${step('/media/flow-pay.png', 'Pay screen with the total.', 'They pay the payer back.')}
          ${step('/media/flow-survey.png', 'Three-question survey after paying.', 'They rate the visit.')}
          ${step('/media/flow-optin.png', 'Unchecked text opt-in.', 'They opt in to your texts.')}
        </ol>
      </section>

      <p class="lede" style="margin:0">Guests tick an unchecked box. Reply STOP to opt out.</p>

      ${calculatorSection()}

      ${form()}
    </div>`;
  // One lead per visit: once either form sends, the contact form and the links to it go away.
  const closeContactForm = (thanks: string) => {
    const f = document.getElementById('leadForm');
    if (f) { if (thanks) f.outerHTML = thanks; else f.remove(); }
    document.querySelectorAll('a[href="#leadForm"]').forEach((a) => a.remove());
  };
  // The calculator shows its own thanks line, so its send just takes the contact form away.
  mountCalculator(utm, () => closeContactForm(''));
  // Email 2 links straight to the calculator. The page is drawn after load, so jump there by hand.
  if (location.hash === '#calculator') requestAnimationFrame(() => document.getElementById('calculator')?.scrollIntoView());

  app.onsubmit = async (e) => {
    e.preventDefault();
    const err = document.getElementById('leadErr') as HTMLElement;
    const btn = document.getElementById('leadSend') as HTMLButtonElement;
    const label = btn.textContent;
    err.hidden = true;
    btn.disabled = true;
    btn.textContent = 'Sending…';
    try {
      await sendRestaurantLead({
        name: inputValue('lName'), restaurant: inputValue('lRestaurant'),
        contact: inputValue('lContact'), message: (document.getElementById('lMessage') as HTMLTextAreaElement).value.trim(),
      });
      trackStep('landing_cta', { page: 'restaurants' });
      // The form is gone, so the two "Sign up" links that jumped to it go too.
      closeContactForm(`<div class="card stack r-thanks" role="status"><h2 class="h" style="margin:0">Got it. We'll reach out within a day to set you up.</h2></div>`);
    } catch (x) {
      err.textContent = (x as Error).message;
      err.hidden = false;
      btn.disabled = false;
      btn.textContent = label;
    }
  };
  app.onclick = (e) => {
    const a = target<HTMLAnchorElement>(e, 'a[href="#leadForm"]');
    if (a) { e.preventDefault(); document.getElementById('leadForm')?.scrollIntoView({ behavior: 'smooth' }); document.getElementById('lName')?.focus({ preventScroll: true }); }
  };
}
